import React, { useEffect, useState } from 'react';
import { CalendarDays, Clock, Phone, Mail, MessageSquare, CheckCircle, Info, ArrowRight, Home as HomeIcon, Shield, Users } from 'lucide-react';
import { Link } from 'react-router-dom';
import { generateMathCaptcha, validateFormSubmission, generateTimestampToken } from '../utils/antiSpam';
export const AdviesGesprek = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    preferredDate: '',
    preferredTime: '',
    message: '',
    consent: false,
    captcha: '',
    formStartTime: Date.now(),
    formToken: generateTimestampToken(),
    honeypot: ''
  });
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [formError, setFormError] = useState('');
  const [captcha, setCaptcha] = useState(generateMathCaptcha());
  const [isSubmitting, setIsSubmitting] = useState(false);
  // Regenerate captcha when the component mounts
  useEffect(() => {
    setCaptcha(generateMathCaptcha());
    setFormData(prev => ({
      ...prev,
      formStartTime: Date.now(),
      formToken: generateTimestampToken()
    }));
  }, []);
  const handleChange = e => {
    const {
      name,
      value,
      type,
      checked
    } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };
  const handleSubmit = async e => {
    e.preventDefault();
    // Prevent double submission
    if (isSubmitting) return;
    setIsSubmitting(true);
    setFormError('');
    // Validate form with anti-spam checks
    const validation = validateFormSubmission(formData.email, formData.captcha, captcha.answer, formData.honeypot, 'adviesgesprek', formData.formStartTime);
    if (!validation.valid) {
      setFormError(validation.error || 'Er is een fout opgetreden. Probeer het opnieuw.');
      setIsSubmitting(false);
      // Generate a new captcha if the current one failed
      setCaptcha(generateMathCaptcha());
      return;
    }
    // Create HTML email content
    const emailHTML = `
      <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto; padding: 20px; color: #333;">
        <h2 style="color: #CDA735; border-bottom: 2px solid #CDA735; padding-bottom: 10px;">Nieuwe adviesgesprek aanvraag</h2>
        <p><strong>Naam:</strong> ${formData.name}</p>
        <p><strong>Email:</strong> ${formData.email}</p>
        <p><strong>Telefoon:</strong> ${formData.phone}</p>
        ${formData.preferredDate ? `<p><strong>Voorkeursdatum:</strong> ${formData.preferredDate}</p>` : ''}
        ${formData.preferredTime ? `<p><strong>Voorkeurstijd:</strong> ${formData.preferredTime}</p>` : ''}
        ${formData.message ? `
        <div style="margin-top: 20px; padding: 15px; background-color: #f7f7f7; border-left: 4px solid #CDA735;">
          <h3 style="margin-top: 0;">Vraag of wensen:</h3>
          <p>${formData.message.replace(/\n/g, '<br>')}</p>
        </div>
        ` : ''}
        <p style="margin-top: 20px; font-size: 12px; color: #777;">Dit bericht is verzonden via het adviesgesprek formulier op veranovaklussen.nl</p>
      </div>
    `;
    // Create webhook payload
    const webhookPayload = {
      emailSubject: 'Nieuwe adviesgesprek aanvraag - Veranova Klussen',
      emailTo: 'info@veranovaklussen.nl',
      formType: 'adviesgesprek',
      formData: {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        preferredDate: formData.preferredDate || '',
        preferredTime: formData.preferredTime || '',
        message: formData.message || '',
        timestamp: Date.now(),
        formToken: formData.formToken
      },
      emailHTML: emailHTML
    };
    const webhookUrl = 'https://hook.eu2.make.com/e9ytbwhleok4r9szhhz2ltro238af7as';
    try {
      const response = await fetch(webhookUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(webhookPayload)
      });
      if (!response.ok) {
        throw new Error('Server responded with an error');
      }
      console.log('Adviesgesprek form webhook sent successfully:', webhookPayload);
      setFormSubmitted(true);
      window.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
    } catch (error) {
      console.error('Error submitting form:', error);
      setFormError('Er is een fout opgetreden bij het verzenden. Probeer het later opnieuw.');
    } finally {
      setIsSubmitting(false);
    }
  };
  // Generate a new captcha
  const refreshCaptcha = () => {
    setCaptcha(generateMathCaptcha());
  };
  return <div className="min-h-screen bg-white w-full" data-id="element-186">
      {/* Hero section */}
      <section className="relative -mt-[100px] pt-52 pb-32 bg-gray-900 overflow-hidden" data-id="element-187">
        <div className="absolute inset-0 bg-cover bg-center opacity-30" style={{
        backgroundImage: "url('https://uploadthingy.s3.us-west-1.amazonaws.com/31qoFLD89xVNfcg4Xki5Xk/491436229_17850212535441316_3063343797506131228_n.jpg')"
      }} data-id="element-188"></div>
        <div className="absolute inset-0 bg-gradient-to-b from-gray-900/90 via-gray-900/70 to-gray-900/90" data-id="element-189"></div>
        <div className="container mx-auto px-6 relative z-10" data-id="element-190">
          <div className="max-w-3xl" data-id="element-191">
            <div className="inline-flex items-center gap-3 mb-6" data-id="element-192">
              <div className="w-8 h-[2px] bg-[#CDA735]" data-id="element-193"></div>
              <span className="text-sm font-medium text-[#CDA735] uppercase tracking-wider" data-id="element-194">
                Adviesgesprek
              </span>
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-6 leading-tight" data-id="element-195">
              Plan een <span className="text-[#CDA735]" data-id="element-196">vrijblijvend</span>{' '}
              adviesgesprek
            </h1>
            <p className="text-xl text-white/80 mb-8 leading-relaxed" data-id="element-197">
              Laat u persoonlijk adviseren door onze experts. Wij denken graag
              met u mee over de beste oplossing voor uw situatie.
            </p>
          </div>
        </div>
      </section>
      {/* Main content */}
      <section className="py-20 bg-white" data-id="element-198">
        <div className="container mx-auto px-6" data-id="element-199">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16" data-id="element-200">
            {/* Form section */}
            <div data-id="element-201">
              {!formSubmitted ? <>
                  {formError && <div className="mb-6 p-4 bg-red-50 border-l-4 border-red-500 text-red-700 rounded" data-id="element-202">
                      <p className="font-medium" data-id="element-203">
                        Er is een probleem opgetreden
                      </p>
                      <p data-id="element-204">{formError}</p>
                    </div>}
                  <form onSubmit={handleSubmit} className="space-y-6" data-id="element-205">
                    {/* Honeypot field - hidden from real users but bots will fill it */}
                    <div className="opacity-0 absolute top-0 left-0 h-0 w-0 -z-10 overflow-hidden" data-id="element-206">
                      <label htmlFor="website" data-id="element-207">Website</label>
                      <input type="text" id="website" name="honeypot" value={formData.honeypot} onChange={handleChange} tabIndex={-1} autoComplete="off" data-id="element-208" />
                    </div>
                    {/* Hidden timestamp token */}
                    <input type="hidden" name="formToken" value={formData.formToken} data-id="element-209" />
                    <input type="hidden" name="formStartTime" value={formData.formStartTime} data-id="element-210" />
                    <div data-id="element-211">
                      <label htmlFor="name" className="block text-gray-700 font-medium mb-2" data-id="element-212">
                        Naam *
                      </label>
                      <input type="text" id="name" name="name" required value={formData.name} onChange={handleChange} className="w-full px-4 py-3 rounded-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#CDA735] focus:border-transparent" placeholder="Uw volledige naam" data-id="element-213" />
                    </div>
                    <div data-id="element-214">
                      <label htmlFor="email" className="block text-gray-700 font-medium mb-2" data-id="element-215">
                        E-mailadres *
                      </label>
                      <div className="relative" data-id="element-216">
                        <input type="email" id="email" name="email" required value={formData.email} onChange={handleChange} className="w-full px-4 py-3 rounded-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#CDA735] focus:border-transparent" placeholder="uw@email.nl" data-id="element-217" />
                        <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none" data-id="element-218">
                          <Mail className="h-5 w-5 text-gray-400" data-id="element-219" />
                        </div>
                      </div>
                    </div>
                    <div data-id="element-220">
                      <label htmlFor="phone" className="block text-gray-700 font-medium mb-2" data-id="element-221">
                        Telefoonnummer *
                      </label>
                      <div className="relative" data-id="element-222">
                        <input type="tel" id="phone" name="phone" required value={formData.phone} onChange={handleChange} className="w-full px-4 py-3 rounded-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#CDA735] focus:border-transparent" placeholder="06 12345678" data-id="element-223" />
                        <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none" data-id="element-224">
                          <Phone className="h-5 w-5 text-gray-400" data-id="element-225" />
                        </div>
                      </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-226">
                      <div data-id="element-227">
                        <label htmlFor="preferredDate" className="block text-gray-700 font-medium mb-2" data-id="element-228">
                          Voorkeursdatum
                        </label>
                        <div className="relative" data-id="element-229">
                          <input type="date" id="preferredDate" name="preferredDate" value={formData.preferredDate} onChange={handleChange} className="w-full px-4 py-3 rounded-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#CDA735] focus:border-transparent" data-id="element-230" />
                          <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none" data-id="element-231">
                            <CalendarDays className="h-5 w-5 text-gray-400" data-id="element-232" />
                          </div>
                        </div>
                      </div>
                      <div data-id="element-233">
                        <label htmlFor="preferredTime" className="block text-gray-700 font-medium mb-2" data-id="element-234">
                          Voorkeurstijd
                        </label>
                        <div className="relative" data-id="element-235">
                          <input type="time" id="preferredTime" name="preferredTime" value={formData.preferredTime} onChange={handleChange} className="w-full px-4 py-3 rounded-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#CDA735] focus:border-transparent" data-id="element-236" />
                          <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none" data-id="element-237">
                            <Clock className="h-5 w-5 text-gray-400" data-id="element-238" />
                          </div>
                        </div>
                      </div>
                    </div>
                    <div data-id="element-239">
                      <label htmlFor="message" className="block text-gray-700 font-medium mb-2" data-id="element-240">
                        Uw vraag of wensen
                      </label>
                      <textarea id="message" name="message" rows={4} value={formData.message} onChange={handleChange} className="w-full px-4 py-3 rounded-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#CDA735] focus:border-transparent" placeholder="Beschrijf kort waar u in geïnteresseerd bent" data-id="element-241"></textarea>
                    </div>
                    {/* Captcha */}
                    <div className="p-4 bg-gray-50 rounded-lg" data-id="element-242">
                      <label className="block text-gray-700 font-medium mb-2" data-id="element-243">
                        Verificatie *
                      </label>
                      <div className="flex items-center space-x-4" data-id="element-244">
                        <div className="bg-white p-3 rounded border border-gray-300 flex-grow max-w-[180px]" data-id="element-245">
                          <p className="text-gray-700 font-medium" data-id="element-246">
                            {captcha.question}
                          </p>
                        </div>
                        <input type="text" name="captcha" value={formData.captcha} onChange={handleChange} required className="w-20 px-3 py-3 rounded-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#CDA735] focus:border-transparent text-center" placeholder="?" data-id="element-247" />
                        <button type="button" onClick={refreshCaptcha} className="p-2 text-gray-500 hover:text-[#CDA735] transition-colors" aria-label="Vernieuw captcha" data-id="element-248">
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" data-id="element-249">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" data-id="element-250" />
                          </svg>
                        </button>
                      </div>
                      <p className="mt-2 text-sm text-gray-500 flex items-center" data-id="element-251">
                        <Shield className="h-4 w-4 mr-1" data-id="element-252" />
                        Anti-spam maatregel
                      </p>
                    </div>
                    <div className="bg-gray-50 p-4 rounded-lg" data-id="element-253">
                      <div className="flex items-start" data-id="element-254">
                        <div className="flex items-center h-5 mt-1" data-id="element-255">
                          <input id="consent" name="consent" type="checkbox" required checked={formData.consent} onChange={handleChange} className="w-5 h-5 text-[#CDA735] bg-white border-2 border-gray-400 rounded focus:ring-[#CDA735] focus:ring-offset-0 cursor-pointer checked:bg-[#CDA735] checked:border-[#CDA735]" data-id="element-256" />
                        </div>
                        <div className="ml-3 text-sm" data-id="element-257">
                          <label htmlFor="consent" className="font-medium text-gray-700" data-id="element-258">
                            Ik ga akkoord met de verwerking van mijn gegevens
                          </label>
                          <p className="text-gray-500" data-id="element-259">
                            Veranova gebruikt uw gegevens alleen om contact met
                            u op te nemen en zal deze niet delen met derden.
                          </p>
                        </div>
                      </div>
                    </div>
                    <div className="bg-gray-50 p-4 rounded-lg flex items-start" data-id="element-260">
                      <Info className="h-5 w-5 text-[#CDA735] mt-0.5 flex-shrink-0" data-id="element-261" />
                      <p className="ml-3 text-sm text-gray-600" data-id="element-262">
                        Na het versturen van het formulier nemen wij binnen 24
                        uur contact met u op om een geschikt moment in te
                        plannen.
                      </p>
                    </div>
                    <button type="submit" disabled={isSubmitting} className={`w-full bg-[#CDA735] text-white font-medium px-8 py-3.5 rounded-md hover:bg-[#B89429] transition-all shadow-lg hover:shadow-[0_0_20px_rgba(205,167,53,0.3)] flex items-center justify-center ${isSubmitting ? 'opacity-70 cursor-not-allowed' : ''}`} data-id="element-263">
                      {isSubmitting ? <>
                          <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" data-id="element-264">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" data-id="element-265"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" data-id="element-266"></path>
                          </svg>
                          Versturen...
                        </> : <>
                          <span data-id="element-267">Plan adviesgesprek</span>
                          <ArrowRight className="ml-2 h-5 w-5" data-id="element-268" />
                        </>}
                    </button>
                  </form>
                </> : <div className="text-center py-12" data-id="element-269">
                  <div className="w-20 h-20 bg-[#CDA735] rounded-full flex items-center justify-center mx-auto mb-8" data-id="element-270">
                    <CheckCircle className="h-10 w-10 text-white" data-id="element-271" />
                  </div>
                  <h2 className="text-2xl font-bold text-gray-900 mb-4" data-id="element-272">
                    Bedankt voor uw aanvraag!
                  </h2>
                  <p className="text-lg text-gray-600 mb-8" data-id="element-273">
                    We nemen binnen 24 uur contact met u op om een geschikt
                    moment in te plannen.
                  </p>
                  <div className="flex flex-col sm:flex-row gap-4 justify-center" data-id="element-274">
                    <Link to="/" className="px-6 py-3 bg-gray-100 hover:bg-gray-200 text-gray-800 rounded-md transition-colors flex items-center justify-center" data-id="element-275">
                      <HomeIcon className="mr-2 h-5 w-5" data-id="element-276" />
                      <span data-id="element-277">Terug naar home</span>
                    </Link>
                    <Link to="/producten" className="px-6 py-3 bg-[#CDA735] hover:bg-[#B89429] text-white rounded-md transition-colors flex items-center justify-center" data-id="element-278">
                      <span data-id="element-279">Bekijk onze producten</span>
                      <ArrowRight className="ml-2 h-5 w-5" data-id="element-280" />
                    </Link>
                  </div>
                </div>}
            </div>
            {/* Benefits section */}
            <div className="lg:pl-16" data-id="element-281">
              <h2 className="text-2xl font-bold text-gray-900 mb-8" data-id="element-282">
                Waarom een adviesgesprek?
              </h2>
              <div className="space-y-6 mb-12" data-id="element-283">
                {[{
                icon: <Users className="h-6 w-6" data-id="element-284" />,
                title: 'Persoonlijk advies',
                description: 'Onze experts denken met u mee en geven eerlijk advies op basis van uw specifieke situatie.'
              }, {
                icon: <Shield className="h-6 w-6" data-id="element-285" />,
                title: 'Vrijblijvend',
                description: 'Het adviesgesprek is geheel vrijblijvend en zonder verplichtingen.'
              }, {
                icon: <HomeIcon className="h-6 w-6" data-id="element-286" />,
                title: 'Bij u thuis',
                description: 'We komen graag bij u langs om de situatie ter plekke te bekijken.'
              }, {
                icon: <MessageSquare className="h-6 w-6" data-id="element-287" />,
                title: 'Direct duidelijkheid',
                description: 'Na het gesprek weet u precies wat de mogelijkheden en kosten zijn.'
              }].map((benefit, index) => <div key={index} className="flex items-start p-6 bg-gray-50 rounded-lg" data-id="element-288">
                    <div className="text-[#CDA735] flex-shrink-0" data-id="element-289">
                      {benefit.icon}
                    </div>
                    <div className="ml-4" data-id="element-290">
                      <h3 className="font-semibold text-gray-900 mb-1" data-id="element-291">
                        {benefit.title}
                      </h3>
                      <p className="text-gray-600" data-id="element-292">{benefit.description}</p>
                    </div>
                  </div>)}
              </div>
              <div className="bg-[#CDA735] text-white p-6 rounded-lg" data-id="element-293">
                <h3 className="font-semibold text-lg mb-2" data-id="element-294">
                  Liever direct contact?
                </h3>
                <p className="mb-4" data-id="element-295">
                  U kunt ons ook direct bereiken via telefoon of e-mail.
                </p>
                <div className="space-y-2" data-id="element-296">
                  <a href="tel:+31625023378" className="flex items-center text-white hover:underline" data-id="element-297">
                    <Phone className="h-5 w-5 mr-2" data-id="element-298" />
                    +316 250 233 78
                  </a>
                  <a href="mailto:info@veranovaklussen.nl" className="flex items-center text-white hover:underline" data-id="element-299">
                    <Mail className="h-5 w-5 mr-2" data-id="element-300" />
                    info@veranovaklussen.nl
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>;
};